import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, radius } from '../../styles/theme';
import ErrorBanner from '../../components/ErrorBanner';
import { searchMatch } from '../../utils/searchMatch';
import { exportCsv } from '../../utils/analytics/export';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const KAPSAM_BADGE = {
  YOK: { label: '🔴 Yok', bg: '#fdecea', color: '#c62828' },
  KISMI: { label: '🟡 Kısmi', bg: '#fff8e1', color: '#b45309' },
  TAM: { label: '🟢 Tam', bg: '#e8f5e9', color: '#2e7d32' },
};

/**
 * Reçete Fire Denetimi
 * Hangi reçetelerde fire (zayi) tanımlı, hangilerinde eksik — iş listesi.
 * Backend: GET /api/recipes/fire-denetimi (VIEW_RECIPE_COSTS guard).
 */
const FireDenetimiPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [arama, setArama] = useState('');
  const [kapsamFiltre, setKapsamFiltre] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/recipes/fire-denetimi`, { headers: getAuthHeaders() });
      if (res.status === 401) {
        window.location.href = '/login';
        return;
      }
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const err = await res.json();
          if (err && err.detail) detail = err.detail;
        } catch (_) { /* yanıt JSON değil */ }
        throw new Error(detail);
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err.message || 'Bilinmeyen hata');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) return <div style={{ padding: 24 }}>Yükleniyor…</div>;

  const ozet = data?.ozet || {};
  const satirlar = data?.satirlar || [];

  const filtreli = satirlar.filter(s => {
    if (kapsamFiltre && s.kapsam !== kapsamFiltre) return false;
    if (!searchMatch(`${s.ad} ${s.recete_kodu}`, arama)) return false;
    return true;
  });

  const handleCsv = () => {
    const rows = filtreli.map(s => ({
      Kod: s.recete_kodu,
      Recete: s.ad,
      Marka: s.marka || '',
      Tip: s.tip,
      Kapsam: s.kapsam,
      Hammadde: s.hammadde_satir,
      FiresizSatir: s.firesiz_satir,
      GuncelMaliyet: s.porsiyon_maliyet == null ? '' : s.porsiyon_maliyet.toFixed(2),
      FoodCostPct: s.food_cost_pct == null ? '' : s.food_cost_pct.toFixed(1),
      SonMaliyetDelta: s.maliyet_degisim == null ? '' : s.maliyet_degisim.toFixed(2),
    }));
    const bugun = new Date().toISOString().slice(0, 10);
    exportCsv(rows, `fire_denetimi_${bugun}.csv`);
  };

  const KapsamBadge = ({ kapsam }) => {
    const meta = KAPSAM_BADGE[kapsam];
    if (!meta) return <span style={{ color: colors.textTertiary }}>—</span>;
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: radius.lg,
        fontSize: '11px', fontWeight: 'bold', background: meta.bg, color: meta.color,
      }}>
        {meta.label}
      </span>
    );
  };

  const chips = [
    { label: `${ozet.yok_sayisi ?? 0} fire'sız`, bg: '#fdecea', color: '#c62828' },
    { label: `${ozet.kismi_sayisi ?? 0} kısmi`, bg: '#fff8e1', color: '#b45309' },
    { label: `${ozet.tam_sayisi ?? 0} tam`, bg: '#e8f5e9', color: '#2e7d32' },
    { label: `${ozet.toplam ?? 0} toplam`, bg: '#eef2f6', color: colors.textSecondary },
  ];

  return (
    <div>
      {error && <div style={{ padding: '0 20px' }}><ErrorBanner error={error} onRetry={fetchData} /></div>}

      {/* Bilgi notu */}
      <div style={{ padding: '12px 20px 0', fontSize: '12px', color: colors.textSecondary, lineHeight: 1.5 }}>
        ℹ️ fire=0 bazen meşrudur (su, tuz). Bu bir iş listesi, yargı değil — 'Yok' neredeyse kesin
        incelenmemiş (öncelik); 'Kısmi/Tam' şefin takdirinde. Fire reçete satırından girilir.
      </div>

      {/* Özet rozetleri */}
      <div style={{ display: 'flex', gap: '8px', padding: '12px 20px', flexWrap: 'wrap' }}>
        {chips.map(c => (
          <span key={c.label} style={{
            display: 'inline-block', padding: '5px 12px', borderRadius: radius.lg,
            fontSize: '13px', fontWeight: 'bold', background: c.bg, color: c.color,
          }}>
            {c.label}
          </span>
        ))}
      </div>

      {/* Filtreler */}
      <div style={{ display: 'flex', gap: '10px', padding: '0 20px 15px', flexWrap: 'wrap' }}>
        <input
          placeholder="Ara (ad, kod...)"
          value={arama}
          onChange={e => setArama(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}
        />
        <select value={kapsamFiltre} onChange={e => setKapsamFiltre(e.target.value)}
          style={{ padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '13px' }}>
          <option value="">Tüm Kapsamlar</option>
          <option value="YOK">Yok</option>
          <option value="KISMI">Kısmi</option>
          <option value="TAM">Tam</option>
        </select>
        <button onClick={handleCsv} disabled={filtreli.length === 0}
          style={{ padding: '8px 16px', border: `1px solid ${colors.border}`, background: colors.surface, color: colors.textSecondary, borderRadius: radius.sm, fontSize: '13px', fontWeight: 'bold', opacity: filtreli.length === 0 ? 0.5 : 1, cursor: filtreli.length === 0 ? 'not-allowed' : 'pointer' }}>
          CSV İndir
        </button>
        <span style={{ alignSelf: 'center', fontSize: 12, color: colors.textTertiary }}>{filtreli.length} kayıt</span>
      </div>

      {/* Tablo */}
      <div style={{ padding: '0 20px', overflowX: 'auto' }}>
        {filtreli.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Kayıt bulunamadı</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '7px', textAlign: 'left' }}>Reçete</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Tip</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>Kapsam</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Hammadde</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Fire'siz</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Güncel Maliyet</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Food Cost %</th>
                <th style={{ padding: '7px', textAlign: 'right' }}>Son Maliyet Δ</th>
                <th style={{ padding: '7px', textAlign: 'center' }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtreli.map(s => (
                <tr key={s.recipe_id} style={{ borderBottom: `1px solid ${colors.border}` }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '10px' }}>
                    <div style={{ fontWeight: 'bold' }}>{s.ad}</div>
                    <div style={{ fontSize: '11px', color: colors.textTertiary, fontFamily: 'monospace' }}>{s.recete_kodu}</div>
                  </td>
                  <td style={{ padding: '7px', textAlign: 'center', color: colors.textSecondary, fontSize: '12px' }}>{s.tip}</td>
                  <td style={{ padding: '7px', textAlign: 'center' }}><KapsamBadge kapsam={s.kapsam} /></td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>{s.hammadde_satir}</td>
                  <td style={{ padding: '7px', textAlign: 'right', fontWeight: s.firesiz_satir > 0 ? 'bold' : 'normal', color: s.firesiz_satir > 0 ? '#c62828' : 'inherit' }}>
                    {s.firesiz_satir}
                  </td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>
                    {s.porsiyon_maliyet == null ? '—' : `${s.porsiyon_maliyet.toFixed(2)} ₺`}
                  </td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>
                    {s.food_cost_pct == null ? '—' : `%${s.food_cost_pct.toFixed(1)}`}
                  </td>
                  <td style={{ padding: '7px', textAlign: 'right' }}>
                    {s.maliyet_degisim == null ? '—' : `${s.maliyet_degisim > 0 ? '+' : ''}${s.maliyet_degisim.toFixed(2)} ₺`}
                  </td>
                  <td style={{ padding: '7px', textAlign: 'center' }}>
                    <button onClick={() => navigate(`/recete/duzenle?id=${s.recipe_id}`)} title="Düzenle"
                      style={{ background: '#e3f2fd', border: 'none', borderRadius: radius.sm, padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>
                      ✎ Düzenle
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default FireDenetimiPage;
